import React, { useEffect } from 'react';
import WTable from '../../../components/WTable';
import { Button, Dropdown, Table } from 'flowbite-react';
import {
    AiOutlineSearch,
    AiOutlinePlus,
    AiOutlineArrowDown,
    AiOutlineCheckCircle,
    AiOutlineClose,
    AiOutlineEllipsis,
    AiOutlineEdit,
    AiOutlineDelete,
    AiOutlineEye
} from 'react-icons/ai';
import PostCodeLoadingState from './PostCodeLoadingState';
import { usePostCodeContext } from '../../../context/postCodeContext';
import { PostalCodeDeleteModal, PostalCodePreviewModal } from '../../../components/modals';
import { toast } from 'react-toastify';
import { useReducerAsState } from '../../../hooks/useReducerAsState';
import { formatHumanReadableDate } from '../../../utils/formatters';
import { PostCodeType } from '../../../types/post_code';
import useDebounce from '../../../hooks/useDebounce';

interface PostCodeResultsProps {
    setIsOpenSideDrawer: (value: boolean) => void;
    onEditPostCode: (id: number) => void;
}

interface State {
    isOpenDeleteModal: boolean;
    postCodeIdToDelete?: number;
    page: number;
    isOpenPreviewModal: boolean;
    postCodeToPreview?: PostCodeType;
    loading: boolean;
    keywords: string | undefined;
}

const PostCodeResults = (props: PostCodeResultsProps) => {
    const { items, total, getAllPostalCodes, deletePostCode } = usePostCodeContext();

    const [state, setState] = useReducerAsState<State>({
        isOpenDeleteModal: false,
        postCodeIdToDelete: undefined,
        isOpenPreviewModal: false,
        postCodeToPreview: undefined,
        page: 1,
        loading: false,
        keywords: undefined
    });

    const debounceSearch = useDebounce(state.keywords ?? '', 500);

    useEffect(() => {
        const onSearch = async (data: string) => {
            if (data.length === 0) {
                _setLoading(true);
                const allPostalCodesError = await getAllPostalCodes('', 1, false);
                _setLoading(false);
                if (allPostalCodesError.length > 0) {
                    toast.error(allPostalCodesError);
                    return;
                }
                return;
            }
            _handlePageChange(1);
            _setLoading(true);
            const allPostalCodesError = await getAllPostalCodes(data, 1, false);
            _setLoading(false);

            if (allPostalCodesError.length > 0) {
                toast.error(allPostalCodesError);
                return;
            }
        };

        onSearch(debounceSearch);
    }, [debounceSearch]);

    useEffect(() => {
        const _onEnterScreen = async () => {
            if (!items || items.length === 0) {
                _setLoading(true);
                const allPostalCodesError = await getAllPostalCodes();
                _setLoading(false);
                if (allPostalCodesError.length > 0) {
                    toast.error(allPostalCodesError);
                    return;
                }
            }
        };

        _onEnterScreen();
    }, [items]);

    const _setLoading = (value: boolean) => setState({ loading: value });

    const _handlePageChange = (page: number) => setState({ page });

    const _loadMorePostCodes = async () => {
        if (state.page === total / 10) return;
        const newPage = state.page + 1;
        _handlePageChange(newPage);
        _setLoading(true);
        const allPostalCodesError = await getAllPostalCodes(undefined, newPage, true);
        _setLoading(false);

        if (allPostalCodesError.length > 0) {
            toast.error(allPostalCodesError);
            return;
        }
    };

    const _toggleSideDrawer = (postCodeId?: number) => {
        props.setIsOpenSideDrawer(true);
        if (postCodeId === undefined) return;
        props.onEditPostCode(postCodeId);
    };

    const _openDeleteModal = (value: boolean, postCodeId?: number) =>
        setState({ isOpenDeleteModal: value, postCodeIdToDelete: postCodeId });

    const _openOverviewModal = (value: boolean, postCode?: PostCodeType) =>
        setState({ isOpenPreviewModal: value, postCodeToPreview: postCode });

    const _handleCloseDeleteModal = () =>
        setState({ isOpenDeleteModal: false, postCodeIdToDelete: undefined });

    const _handleClosePreviewModal = () =>
        setState({ isOpenPreviewModal: false, postCodeToPreview: undefined });

    const _deletePostCode = async (postCodeId?: number) => {
        if (postCodeId === undefined) return;
        const isDeletedMessage = await deletePostCode(postCodeId);
        _handleCloseDeleteModal();
        if (isDeletedMessage.length > 0) {
            toast.error(isDeletedMessage);
            return;
        }
        toast.success('Código postal borrado correctamente');
    };

    const _renderContent = () => {
        return items.map((item, index) => (
            <Table.Row key={index}>
                <Table.Cell>{item.title}</Table.Cell>
                <Table.Cell>{item.postal_code}</Table.Cell>
                <Table.Cell>
                    {item.active === 1 ? (
                        <AiOutlineCheckCircle color="green" size={16} />
                    ) : (
                        <AiOutlineClose color="red" size={16} />
                    )}
                </Table.Cell>
                <Table.Cell>{formatHumanReadableDate(item.fetched_at ?? '')}</Table.Cell>
                <Table.Cell>{formatHumanReadableDate(item.ends_at ?? '')}</Table.Cell>
                <Table.Cell>
                    <Dropdown
                        dismissOnClick={false}
                        label={<AiOutlineEllipsis size={16} />}
                        size="xs"
                        color="gray">
                        <Dropdown.Header>
                            <span className="block text-sm">Acciones</span>
                        </Dropdown.Header>
                        <Dropdown.Item onClick={() => _toggleSideDrawer(item.id)}>
                            <AiOutlineEdit className="mr-1 h-5 w-5" size={16} />
                            Editar
                        </Dropdown.Item>
                        <Dropdown.Item
                            className="text-red-500"
                            onClick={() => _openDeleteModal(true, item.id)}>
                            <AiOutlineDelete className="mr-1 h-5 w-5" size={16} />
                            Borrar
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                            onClick={() => _openOverviewModal(true, item)}
                            className="text-green-500">
                            <AiOutlineEye className="mr-1 h-5 w-5" size={16} />
                            Visualizar
                        </Dropdown.Item>
                    </Dropdown>
                </Table.Cell>
            </Table.Row>
        ));
    };

    const render = () => {
        if (state.loading) return <PostCodeLoadingState />;

        if (items.length === 0) return <p>No se encontraron resultados</p>;

        return (
            <div className="w-full">
                <div className="w-full h-96 overflow-auto">
                    <WTable
                        heads={['Título', 'CP', 'Activo', 'Última actualización', 'Vigencia', '']}>
                        {_renderContent()}
                    </WTable>
                </div>
            </div>
        );
    };

    return (
        <div className="px-6 py-10 w-full h-full">
            <div className="flex flex-row justify-between items-center mb-4">
                <div className="relative w-full max-w-xs">
                    <input
                        type="text"
                        placeholder="Buscar..."
                        className="w-full rounded-full border bg-gray-100 border-gray-200 px-4 py-2 pl-10 text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        value={state.keywords}
                        onChange={(e) => setState({ keywords: e.target.value })}
                    />
                    <AiOutlineSearch className="absolute left-3 top-2.5 text-gray-500" size={20} />
                </div>
                <Button size="sm" color="dark" onClick={() => _toggleSideDrawer()}>
                    <AiOutlinePlus className="mr-1 h-5 w-5" size={16} />
                    Añadir nuevo
                </Button>
            </div>

            {render()}

            <div className="w-full py-5 flex flex-row justify-center items-center">
                <Button
                    onClick={_loadMorePostCodes}
                    size="xs"
                    color="transparent"
                    className="text-blue-800">
                    Ver más códigos postales
                    <AiOutlineArrowDown size={16} />
                </Button>
            </div>

            <PostalCodeDeleteModal
                open={state.isOpenDeleteModal && state.postCodeIdToDelete !== undefined}
                onClose={_handleCloseDeleteModal}
                onDelete={_deletePostCode}
                postCodeId={state.postCodeIdToDelete ?? -1}
            />

            <PostalCodePreviewModal
                open={state.isOpenPreviewModal && state.postCodeToPreview !== undefined}
                onClose={_handleClosePreviewModal}
                postCode={state.postCodeToPreview ?? undefined}
            />
        </div>
    );
};

export default PostCodeResults;
